import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { Icon } from '../utils/icons';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const card = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
const hexToRgba = (hex, a) => { const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16); return `rgba(${r},${g},${b},${a})`; };
function KPI({ label, value, iconName, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: hexToRgba(color, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={iconName} size={22} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontSize: '26px', fontWeight: '900', color: '#111827', lineHeight: 1 }}>{value}</div>
      </div>
    </div>
  );
}

// Charts use real data from API

function fmt(v, t) {
  if (v === null || v === undefined) return '—';
  if (t === '$') return '$' + (v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : v >= 1e3 ? Math.round(v / 1e3) + 'K' : Math.round(v));
  if (t === '%') return (v || 0) + '%';
  return v.toLocaleString();
}

function FunnelViz({ stages }) {
  const max = Math.max(...stages.map((s) => s.count), 1);
  const colors = ['#3a6ad9', '#3a6ad9', '#acf44e', '#d97706'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {stages.map((s, i) => (
        <div key={s.label}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{s.label}</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{s.count.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ flex: 1, height: '10px', backgroundColor: '#f3f4f6', borderRadius: '5px', overflow: 'hidden' }}>
              <div style={{ width: `${(s.count / max) * 100}%`, height: '100%', backgroundColor: colors[i] || '#3a6ad9', borderRadius: '5px', transition: 'width 0.5s' }} />
            </div>
            {i > 0 && stages[i - 1].count > 0 && (
              <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '34px', textAlign: 'right' }}>
                {Math.round((s.count / stages[i - 1].count) * 100)}%
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DashSales() {
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState({
    applicationsStarted: 0,
    applicationsSubmitted: 0,
    applicationsApproved: 0,
    applicationsClosed: 0,
    approvalRate: 0,
    pipelineVolume: 0,
  });
  const [funnel, setFunnel] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [s, ov] = await Promise.all([
        axios.get(`${API}/api/admin/dashboard/sales-data`, { headers }),
        axios.get(`${API}/api/admin/dashboard/overview-data`, { headers }),
      ]);
      setData(s.data);
      setFunnel(ov.data.funnel || []);
    } catch {}
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#9ca3af', fontSize: '14px' }}>Loading…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Sales</h1>
          <p style={{ fontSize: '13px', color: '#6b7280' }}>Pipeline health &amp; conversion performance</p>
        </div>

        {/* Stat Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '28px' }}>
          <KPI label="Apps Started" value={data.applicationsStarted} iconName="document" color="#3a6ad9" />
          <KPI label="Apps Submitted" value={data.applicationsSubmitted} iconName="arrow" color="#3a6ad9" />
          <KPI label="Apps Approved" value={data.applicationsApproved} iconName="check-circle" color="#acf44e" />
          <KPI label="Loans Closed" value={data.applicationsClosed} iconName="key" color="#acf44e" />
          <KPI label="Approval Rate" value={fmt(data.approvalRate, '%')} iconName="chart" color="#ffdd86" />
          <KPI label="Pipeline Volume" value={fmt(data.pipelineVolume, '$')} iconName="currency" color="#3a6ad9" />
        </div>

        {/* Charts 2×2 */}
        <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
          {/* Application Funnel (real) */}
          <div style={card}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>Application Funnel</h3>
            {funnel.length > 0 ? <FunnelViz stages={funnel} /> : (
              <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>No data yet</div>
            )}
          </div>

          {/* Pipeline Summary */}
          <div style={{ ...card, gridColumn: '1 / -1' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Pipeline Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px' }}>
              {[
                { label: 'Started', value: data.applicationsStarted, color: '#3a6ad9' },
                { label: 'Submitted', value: data.applicationsSubmitted, color: '#3a6ad9' },
                { label: 'Approved', value: data.applicationsApproved, color: '#acf44e' },
                { label: 'Closed', value: data.applicationsClosed, color: '#acf44e' },
                { label: 'Approval Rate', value: fmt(data.approvalRate, '%'), color: '#ffdd86' },
                { label: 'Pipeline Volume', value: fmt(data.pipelineVolume, '$'), color: '#3a6ad9' },
              ].map(r => (
                <div key={r.label} style={{ padding: '14px 16px', borderRadius: '10px', backgroundColor: '#f9fafb', border: '1px solid #f3f4f6' }}>
                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{r.label}</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: r.color }}>{r.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
